import React, { useCallback, useEffect, useState } from "react";
import { MdClear } from "react-icons/md";
import Swal from "sweetalert2";

import { Loading } from "../../../../components/Loading";
import ProfileEnum from "../../../../enums/ProfileEnum";
import ShippingStagesEnum from "../../../../enums/ShippingStagesEnum";
import { BrComerceApiService } from "../../../../services/BrComerceApiService";
import store from "../../../../store/store";
import debounce from "../../../../utils/debounce";
import useGetWindowWidth from "../../../../utils/useGetWindowWidth";
import { CartCardShipping } from "../../components/CartCardShipping";
import styles from "./styles.module.scss";
import InputSelectAsync from "../../../../components/InputSelectAsync";
import InfiniteScroll from "react-infinite-scroll-component";
import LoadInfiniteScroll from "../../../../components/LoadInfiniteScroll";
import ScrollDiv from "../../components/ScrollDiv";
import DatePicker from "../../../../components/Input/DatePicker";
import moment from "moment";
import { set } from "lodash";
import { orderBy } from "lodash";

const PrintTags = () => {
  window.setPageTitle("Imprimir Etiquetas");

  const api = new BrComerceApiService();
  const { auth } = store.getState();
  const { user } = auth;
  const widthWindows = useGetWindowWidth();
  const isAdmin = user.profile === ProfileEnum.ADMIN.value;

  const [loading, setLoading] = useState(false);
  const [packagesFiltered, setPackagesFiltered] = useState([]);
  const [searchStringCollect, setSearchStringCollect] = useState("");
  const [packagesSelectedId, setPackagesSelectedId] = useState([]);
  const [isSelectedAllCartItems, setIsSelectedAllCartItems] = useState(true);
  const [packagesExpand, setPackagesExpand] = useState([]);
  const [shippings, setShippings] = useState([]);
  const [searchClient, setSearchClient] = useState(null);
  const [loadingInfinit, setLoadingInfinit] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [periodInitialDate, setPeriodInitialDate] = useState(null);
  const [periodFinalDate, setPeriodFinalDate] = useState(null);

  const handleClearSearchFilter = () => {
    setSearchStringCollect("");
  };

  const handlePackageSelection = useCallback(
    packId => {
      const findedPackage = packagesSelectedId.find(id => id === packId);

      if (findedPackage) {
        setPackagesSelectedId(prevState =>
          prevState.filter(id => id !== packId)
        );
      } else {
        setPackagesSelectedId(prevState => [...prevState, packId]);
      }
    },
    [packagesSelectedId]
  );

  const handleToggleCheckAllCartItems = useCallback(() => {
    if (isSelectedAllCartItems) {
      setIsSelectedAllCartItems(false);
      setPackagesSelectedId([]);
      return;
    }

    setIsSelectedAllCartItems(true);
    setPackagesSelectedId(
      packagesFiltered
        .filter(
          p => p.idShippingStatus !== ShippingStagesEnum.OBJETO_CANCELADO.id
        )
        .map(s => s.id)
    );
  }, [isSelectedAllCartItems, packagesFiltered]);

  const handlePrintTags = useCallback(
    async () => {
      const data = packagesSelectedId.map(pack => {
        return pack;
      });

      try {
        setLoading(true);

        window.localStorage.setItem(
          "Brcom:Etiquetas",
          JSON.stringify(data)
        );

        const win = window.open("/etiquetas", "_blank");

        win.focus();
      } catch (e) {
        console.error(e.message);
        if (e.message === "win is null") {
          Swal.fire({
            icon: "error",
            title: "Pop-Up",
            text: "Por favor habilite os pop-ups no seu navegador."
          });
          return;
        }

        Swal.fire({
          icon: "error",
          title: "Etiquetas",
          text:
            "Houve um erro ao carregar etiquetas, tente novamente mais tarde."
        });
      } finally {
        setLoading(false);
      }
    },
    [packagesSelectedId]
  );

  const handlePackageForSearch = useCallback(
    debounce(searchString => {
      const packagesFiltered = shippings.filter(pack => {
        let searchableInfoInPackage = null;
        if (searchString) {
          searchableInfoInPackage = {
            sro: pack.sro,
            name: pack.name,
            orderNumber: pack.orderNumber,
            type: pack.type,
            originCepFormatted: `${pack.origin.zip.substring(
              0,
              5
            )}-${pack.origin.zip.substring(5, 8)}`,
            originCep: pack.origin.zip,
            destinationCepFormatted: `${pack.destination.zip.substring(
              0,
              5
            )}-${pack.destination.zip.substring(5, 8)}`,
            destinationCep: pack.destination.zip,
            originName: pack.origin.name,
            destinationName: pack.destination.name
          };
        }

        const packHasInfoSearchedSetted =
          searchableInfoInPackage !== null
            ? Object.values(searchableInfoInPackage)
              .filter(value => !!value)
              .some(
                packageInfo =>
                  !!packageInfo &&
                  String(packageInfo)
                    .toLowerCase()
                    .includes(searchString.toLowerCase())
              )
            : true;

        return packHasInfoSearchedSetted;
      });

      setPackagesFiltered(packagesFiltered);
    }, 100),
    [shippings, searchStringCollect]
  );

  const loadShippingsByUser = useCallback(async () => {
    try {
      setLoadingInfinit(true);

      const results = await api.makeHttpRequest({
        method: "GET",
        url: `shipping/tagsByUser`,
        params: {
          subUser: searchClient ? searchClient.sub : null,
          page,
          resultsPerPage: 20,
          ...(periodInitialDate && { initialDate: periodInitialDate }),
          ...(periodFinalDate && { finalDate: periodFinalDate }),
        }
      });

      if (results?.length === 20) {
        setPage(page + 1)
      } else {
        setHasMore(false);
      }

      const resultsDeduplicated = [];

      for await (const result of results) {
        const duplicated = shippings.find(i => i?.id === result?.id) || "";

        if (!duplicated) {
          resultsDeduplicated.push(result)
        }
      }

      setShippings(prevState => [...prevState, ...results]);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Houve um erro ao carregar a página, tente novamente mais tarde."
      });
      console.error("Houve um erro ao carregar a página", e);
    } finally {
      setLoadingInfinit(false);
    }
  }, [api, searchClient, page, shippings, periodInitialDate, periodFinalDate,]);

  useEffect(() => {
    if (!shippings) return;

    if (!searchStringCollect) {
      setPackagesFiltered(shippings);
      return;
    }

    if (searchStringCollect) {
      const searchString = searchStringCollect.trim();
      handlePackageForSearch(searchString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippings, searchStringCollect]);

  useEffect(() => {
    if (!searchClient) return;

    setShippings([]);
    setPage(1);
    setHasMore(true);
    setPackagesFiltered([]);
    setPackagesSelectedId([]);
    setIsSelectedAllCartItems(false);
    loadShippingsByUser();

  }, [searchClient, , periodInitialDate, periodFinalDate]);

  const handlePackageExpand = useCallback(
    pack => {
      const find = packagesExpand.find(p => p.id === pack.id);
      if (find) {
        const newPacks = packagesExpand.filter(p => p.id !== pack.id);
        setPackagesExpand([...newPacks]);
      } else {
        packagesExpand.push(pack);
        setPackagesExpand([...packagesExpand]);
      }
    },
    [packagesExpand]
  );

  const handleOnChangeCalendarPeriod = e => {
    const inicio = e.dateStart ? e.dateStart?.split("/") :
      e.length ? e[0]?.split("/") : undefined;
    const fim = e.dateEnd ? e.dateEnd?.split("/") :
      e.length ? e[1]?.split("/") : undefined;

    if (inicio && fim) {
      setPeriodInitialDate(
        new Date(Number(inicio[2]), Number(inicio[1] - 1), Number(inicio[0]))
      );
      setPeriodFinalDate(
        new Date(Number(fim[2]), Number(fim[1] - 1), Number(fim[0]))
      );
    }
  };

  const handleClearPeriodFilter = () => {
    setPeriodInitialDate("");
    setPeriodFinalDate("");
  };

  return (
    <>
      <Loading loading={loading} />
      <div className={styles.main}>
        <div className={styles.container}>
          <div className={styles.title}>
            <h2>
              <strong>Objetos para impressão</strong>
            </h2>
          </div>
          <div className={styles.actionButtons}>

            <div className={styles.inputClient}>
              <InputSelectAsync
                search={searchClient}
                setSearch={setSearchClient}
              />
            </div >

            <div className={styles.input}>
              <input
                type="text"
                name="search"
                placeholder="Filtrar resultados"
                onChange={e => setSearchStringCollect(e.target.value)}
                value={searchStringCollect}
              />

              {!!searchStringCollect?.length && (
                <MdClear
                  style={{ cursor: "pointer" }}
                  onClick={handleClearSearchFilter}
                />
              )}
            </div>
            <div className={`${styles.input} ${styles.selectPicker}`}>
              <DatePicker
                className={`datePickerConfig ${styles.inputPicker}`}
                change={handleOnChangeCalendarPeriod}
                placeholder="Filtrar por período"
                showClearButton={!!periodInitialDate && !!periodFinalDate}
                onClearFields={handleClearPeriodFilter}
                dateStart={periodInitialDate}
                dateFinal={periodFinalDate}
                range
              />
            </div>
          </div>

          <div className={`${styles.buttonsContainer}`}>
            <button
              type="button"
              onClick={handleToggleCheckAllCartItems}
              disabled={!shippings.length}
            >
              {(isSelectedAllCartItems &&
                packagesSelectedId.length) ||
                packagesSelectedId.length ===
                packagesFiltered.length ?
                "Desmarcar todos" : "Marcar todos"}
            </button>
            <button
              onClick={() => handlePrintTags(shippings)}
              disabled={!packagesSelectedId.length}
            >
              Imprimir
            </button>
          </div>


          <ScrollDiv data={shippings}>
            <InfiniteScroll
              dataLength={shippings.length}
              next={loadShippingsByUser}
              hasMore={hasMore}
              loader={loadingInfinit && !loading && <LoadInfiniteScroll />}
              scrollableTarget="scrollDiv"
              className={styles.infiniteScroll}
            >{
                packagesFiltered?.map(pack => (
                  <div className={styles.collectWrapper}>
                    <CartCardShipping
                      widthWindows={widthWindows}
                      key={pack.id}
                      shipping={pack}
                      isSelected={packagesSelectedId.find(id => pack.id === id)}
                      isExpand={packagesExpand.find(
                        packExpand => pack.id === packExpand.id
                      )}
                      onClick={() => handlePackageSelection(pack.id)}
                      onExpand={() => handlePackageExpand(pack)}
                      setLoading={setLoading}
                      isAdmin={isAdmin}
                      linkTracking
                    />
                  </div>
                ))
              }
            </InfiniteScroll>
          </ScrollDiv>
          {!shippings.length && !loadingInfinit && (
            <div className={styles.collectWrapper}>
              <div className={styles.notFoundOrEmptyContainer}>
                <p>
                  {!!shippings?.length && !!searchStringCollect
                    ? "Não foram encontradas envios para essa pesquisa."
                    : "Selecione o cliente."}
                </p>
              </div>
            </div>
          )}
        </div>
      </div >
    </>
  );
};

export { PrintTags };
